import React, { useEffect, useRef, useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Bars3Icon,
  HomeIcon,
  ArrowLeftOnRectangleIcon,
  NewspaperIcon,
  UsersIcon,
  UserCircleIcon,
  PhotoIcon,
  SquaresPlusIcon,
  ShieldCheckIcon,
  CubeIcon,
  VideoCameraIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  DocumentPlusIcon,
  CalendarDateRangeIcon,
  ClipboardDocumentIcon,
  KeyIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  XMarkIcon,
  Squares2X2Icon,
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  ArrowsRightLeftIcon,
  MagnifyingGlassCircleIcon,
  MegaphoneIcon,
} from "@heroicons/react/24/outline";
import { ChevronDoubleLeftIcon, UserIcon } from "@heroicons/react/16/solid";
import { setUsers } from "../features/admin/UserSlice";
import UserModalComponent from "../components/admin/UserModal";
import PartnerModal from "../components/admin/PartnerModal";

// 👉 Redux chỉ cho workspace
import {
  toggleWorkspace as toggleWorkspaceAction,
  setWorkspace as setWorkspaceAction,
  resetWorkspace as resetWorkspaceAction,
} from "../features/admin/WorkspaceSlice";

/* ---------- Helpers ---------- */
const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");

// const isApproved = localStorage.getItem("isApproved") === "true";
const companyId = localStorage.getItem("companyId");

const hasPermission = (requiredPermission) => {
  if (userId == 1) return true;
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

let userType = localStorage.getItem("type_user");
if (hasPermission("CONTENT_MANAGE")) {
  userType = "admin";
}

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    window.innerWidth < 1024 ? false : true,
  );
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isOption, setIsOption] = useState(false);
  const dropdownRef = useRef(null);

  // Lấy workspace từ store (an toàn với fallback)
  const workspace = useSelector((state) => state.workspace?.value ?? "admin");

  // Đóng dropdown khi click ngoài
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Đóng sidebar khi nhấn Esc (chỉ trên mobile)
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape" && isSidebarOpen && window.innerWidth < 1024) {
        setIsSidebarOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isSidebarOpen]);

  // Ép workspace theo rule: user => member, không có companyId => admin
  useEffect(() => {
    if (userType === "user") {
      dispatch(setWorkspaceAction("member"));
    } else if (!companyId || companyId === "null") {
      dispatch(setWorkspaceAction("admin"));
    }
  }, [dispatch]);

  const toggleWorkspace = () => {
    if (userType === "user") return; // user không toggle
    if (!companyId || companyId === "null") return; // không có companyId không toggle
    if (workspace == "admin") {
      setIsSidebarOpen(false);
      navigate("/admin/dashboard");
    } else {
      setIsSidebarOpen(true);
    }
    dispatch(toggleWorkspaceAction());
  };

  const handleCloseUserModal = () => setIsUserModalOpen(false);
  const handleClosePartnerModal = () => setIsPartnerModalOpen(false);

  const handleSaveUser = (user) => {
    if (user.id) {
      dispatch(setUsers(user));
    } else {
      const newUser = { ...user, id: Date.now() };
      dispatch(setUsers(newUser));
    }
    handleCloseUserModal();
  };

  const handleMenuItemClick = () => {
    if (window.innerWidth < 1024) setIsSidebarOpen(false);
    setIsOpen(false);
  };

  const handleLogout = () => {
    // reset workspace về admin
    dispatch(resetWorkspaceAction());

    localStorage.removeItem("access_token_bccu");
    localStorage.removeItem("permissions");
    localStorage.removeItem("full_name");
    localStorage.removeItem("type_user");
    localStorage.removeItem("userId");
    localStorage.removeItem("companyId");
    localStorage.removeItem("userPhone");
    localStorage.removeItem("userEmail");
    localStorage.setItem("cart", JSON.stringify([]));
    window.location.href = "/login";
  };

  return (
    <div className="flex h-screen">
      {/* Overlay: xuất hiện trên mobile khi sidebar mở — click để đóng */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-10 bg-black/50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      {hasPermission("CONTENT_MANAGE") && (
        <aside
          className={`fixed ${
            isSidebarOpen
              ? "translate-x-0 lg:static lg:translate-x-0"
              : "-translate-x-full"
          } transition-transform duration-300 w-64 lg:w-64 h-screen overflow-y-auto shadow-lg lg:shadow-none z-20 bg-base-100 lg:bg-transparent`}
          aria-label="Sidebar navigation"
        >
          <button
            className="lg:hidden absolute top-4 right-4"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <XMarkIcon className="h-6 w-6 text-primary" />
          </button>

          <ul className="menu menu-vertical p-4 space-y-2 mt-12 max-sm:mb-40 lg:mt-0">
            <li>
              <Link to="/admin/dashboard" onClick={handleMenuItemClick}>
                <Squares2X2Icon className="h-5 w-5 text-red-600" />
                <span className="lg:block">Tổng quan</span>
              </Link>
            </li>

            {hasPermission("COMPANY_SHOW") && (
              <li>
                <Link to="/admin/partner" onClick={handleMenuItemClick}>
                  <BuildingOffice2Icon className="h-5 w-5 text-blue-600" />
                  <span className="lg:block">Hội viên</span>
                </Link>
              </li>
            )}

            {hasPermission("USER_SHOW") && (
              <li>
                <Link to="/admin/users" onClick={handleMenuItemClick}>
                  <UsersIcon className="h-5 w-5 text-yellow-600" />
                  <span className="lg:block">Người dùng</span>
                </Link>
              </li>
            )}

            {hasPermission("MEMBERSHIP_FEE_SHOWS") && (
              <li>
                <Link to="/admin/membershipfees" onClick={handleMenuItemClick}>
                  <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                  <span className="lg:block">Hội phí</span>
                </Link>
              </li>
            )}

            {hasPermission("POST_CREATE") && (
              <li>
                <Link to="/admin/notification" onClick={handleMenuItemClick}>
                  <BellIcon className="h-5 w-5 text-red-600" />
                  <span className="lg:block">Hoạt động</span>
                </Link>
              </li>
            )}

            {hasPermission("REGISTER_SHOW") && (
              <li>
                <Link to="/admin/registration" onClick={handleMenuItemClick}>
                  <DocumentPlusIcon className="h-5 w-5 text-blue-600" />
                  <span className="lg:block">Đăng ký hoạt động</span>
                </Link>
              </li>
            )}

            {hasPermission("POST_CREATE") && (
              <li>
                <Link to="/admin/posts" onClick={handleMenuItemClick}>
                  <NewspaperIcon className="h-5 w-5 text-green-500" />
                  <span className="lg:block">Bài viết</span>
                </Link>
              </li>
            )}

            {hasPermission("COMMENT_SHOW") && (
              <li>
                <Link to="/admin/feedback" onClick={handleMenuItemClick}>
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-yellow-500" />
                  <span className="lg:block">Góp ý bài viết</span>
                </Link>
              </li>
            )}

            {hasPermission("PRODUCT_SHOW_LIST") && (
              <li>
                <Link to="/admin/product" onClick={handleMenuItemClick}>
                  <CubeIcon className="h-5 w-5 text-red-600" />
                  <span className="lg:block">Sản phẩm hội viên</span>
                </Link>
              </li>
            )}

            {hasPermission("BILL_SHOW_TOTAL_VALUE") && (
              <li>
                <Link to="/admin/order" onClick={handleMenuItemClick}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="yellow"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-receipt w-5 h-5 stroke-blue-600"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2m4 -14h6m-6 4h6m-2 4h2" />
                  </svg>
                  <span className="lg:block">Giá trị giao dịch</span>
                </Link>
              </li>
            )}

            {hasPermission("CONTACT_SHOW") && (
              <li>
                <Link to="/admin/contact" onClick={handleMenuItemClick}>
                  <ClipboardDocumentIcon className="h-5 w-5 text-green-600" />
                  <span className="lg:block">Liên hệ</span>
                </Link>
              </li>
            )}

            {hasPermission("PRODUCT_REQUEST_SHOW") && (
              <li>
                <Link to="/admin/product-request" onClick={handleMenuItemClick}>
                  <MagnifyingGlassCircleIcon className="h-5 w-5 text-green-600" />
                  <span className="lg:block">Yêu cầu tìm sản phẩm</span>
                </Link>
              </li>
            )}

            {hasPermission("MEDIA_SHOW_LIST") && (
              <>
                <li>
                  <Link to="/admin/media" onClick={handleMenuItemClick}>
                    <PhotoIcon className="h-5 w-5 text-green-600" />
                    <span className="lg:block">Hình ảnh</span>
                  </Link>
                </li>
                <li>
                  <Link to="/admin/videos" onClick={handleMenuItemClick}>
                    <VideoCameraIcon className="h-5 w-5 text-red-600" />
                    <span className="lg:block">Video</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/admin/advertisement-medias"
                    onClick={handleMenuItemClick}
                  >
                    <MegaphoneIcon className="h-5 w-5 text-purple-600" />
                    <span className="lg:block">Quản lý quảng cáo</span>
                  </Link>
                </li>
              </>
            )}

            {hasPermission("POINT_SETTING") && (
              <li>
                <Link to="/admin/point-log" onClick={handleMenuItemClick}>
                  <CalendarDateRangeIcon className="h-5 w-5 text-yellow-600" />
                  <span className="lg:block">Chấm điểm hoạt động</span>
                </Link>
              </li>
            )}

            {(hasPermission("ROLE_SHOW") ||
              hasPermission("CATEGORY_SHOW") ||
              hasPermission("PERMISSIONS_SHOW") ||
              hasPermission("POINT_SETTINGS") ||
              hasPermission("WELCOME_CARD_SHOW") ||
              hasPermission("EVENT_INVITATION_CARD_SHOW") ||
              hasPermission("SETTING_SHOW") ||
              hasPermission("CRITERIA_SHOW")) && (
              <li>
                <div
                  className="flex justify-between"
                  onClick={() => setIsOption(!isOption)}
                >
                  <div className="flex gap-2.5 items-center">
                    <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
                    <span className="lg:block">Cài đặt</span>
                  </div>
                  <ChevronDownIcon className="h-5 w-5" />
                </div>
                {isOption && (
                  <div className="flex flex-col items-start">
                    {hasPermission("ROLE_SHOW") && (
                      <li>
                        <Link to="/admin/roles" onClick={handleMenuItemClick}>
                          <UserCircleIcon className="h-5 w-5 text-green-600" />
                          <span className="lg:block">Chức vụ</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission("CATEGORY_SHOW") && (
                      <li>
                        <Link
                          to="/admin/category"
                          onClick={handleMenuItemClick}
                        >
                          <SquaresPlusIcon className="h-5 w-5 text-yellow-500" />
                          <span className="lg:block">Tạo danh mục</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission("PERMISSIONS_SHOW") && (
                      <li>
                        <Link
                          to="/admin/permission-show"
                          onClick={handleMenuItemClick}
                        >
                          <ShieldCheckIcon className="h-5 w-5 text-red-600" />
                          <span className="lg:block">Phân quyền danh mục</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission("POINT_SETTINGS") && (
                      <li>
                        <Link
                          to="/admin/point-criteria-setting"
                          onClick={handleMenuItemClick}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-pointer-up w-5 h-5 stroke-red-600"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M15.984 13.428l-1.206 -1.206l3.113 -2.09a1.2 1.2 0 0 0 -.309 -2.228l-13.582 -3.904l3.904 13.563a1.2 1.2 0 0 0 2.228 .308l2.09 -3.093l1.217 1.217" />
                            <path d="M19 22v-6" />
                            <path d="M22 19l-3 -3l-3 3" />
                          </svg>
                          <span className="lg:block">
                            Tiêu chí điểm hoạt động
                          </span>
                        </Link>
                      </li>
                    )}
                    {hasPermission("WELCOME_CARD_SHOW") && (
                      <li>
                        <Link
                          to="/admin/welcome-card-template"
                          onClick={handleMenuItemClick}
                        >
                          <ClipboardDocumentIcon className="h-5 w-5 text-blue-600" />
                          <span className="lg:block">Thiệp chào mừng</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission("EVENT_INVITATION_CARD_SHOW") && (
                      <li>
                        <Link
                          to="/admin/event-invitation-card-template"
                          onClick={handleMenuItemClick}
                        >
                          <ClipboardDocumentIcon className="h-5 w-5 text-purple-600" />
                          <span className="lg:block">Thiệp sự kiện</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission("SETTING_SHOW") && (
                      <li>
                        <Link
                          to="/admin/system-settings"
                          onClick={handleMenuItemClick}
                        >
                          <Cog6ToothIcon className="h-5 w-5 text-purple-600" />
                          <span className="lg:block">Cài đặt hệ thống</span>
                        </Link>
                      </li>
                    )}
                  </div>
                )}
              </li>
            )}
          </ul>
        </aside>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:relative ">
        {/* Navbar */}
        <nav className="bg-base-200 shadow-lg py-2 px-4 flex justify-end items-center z-50 ">
          <div
            className={`flex space-x-1 items-center ${
              hasPermission("CONTENT_MANAGE")
                ? "justify-end"
                : "justify-between"
            } max-md:w-full`}
          >
            <>
              {userType !== "user" && companyId && companyId !== "null" ? (
                <button
                  onClick={toggleWorkspace}
                  className="btn btn-outline btn-sm  normal-case"
                  title={
                    workspace === "admin"
                      ? "Chuyển đến giao diện hội viên"
                      : "Chuyển đến giao diện quản trị"
                  }
                >
                  <ArrowsRightLeftIcon className="h-5 w-5" />
                </button>
              ) : (
                <div></div>
              )}
            </>

            {/* Xin chào + dropdown */}
            <div className="flex gap-2 items-center">
              <div>
                <button
                  className="text-lg flex"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <div className="flex flex-col md:flex-row">
                    <span className="text-sm content-center min-w-20">
                      Xin chào
                    </span>{" "}
                    <div className="flex">
                      <span className="font-semibold ms-1">
                        {localStorage.getItem("full_name")}
                      </span>
                      <UserIcon className="h-6 w-6 ms-1" />
                      <ChevronDownIcon className=" h-6 w-3" />
                    </div>
                  </div>
                </button>
              </div>

              <div
                className=" relative inline-block text-left"
                ref={dropdownRef}
              >
                {isOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-base-300 rounded-lg shadow-lg z-10">
                    <ul className="p-3 py-4 space-y-5">
                      {userId != 1 && (
                        <li>
                          <Link
                            onClick={() => {
                              setIsUserModalOpen(true);
                            }}
                            className="flex items-center gap-1"
                          >
                            <UserIcon className="h-5 w-5" />
                            <span className="lg:block">Thông tin cá nhân</span>
                          </Link>
                        </li>
                      )}
                      {userId != 1 && companyId != "null" && (
                        <li>
                          <Link
                            onClick={() => {
                              setIsPartnerModalOpen(true);
                            }}
                            className="flex items-center gap-1"
                          >
                            <BuildingOffice2Icon className="h-5 w-5" />
                            <span className="lg:block">Thông tin đơn vị</span>
                          </Link>
                        </li>
                      )}
                      <li>
                        <Link
                          to="/admin/change-password"
                          onClick={handleMenuItemClick}
                          className="flex items-center gap-1"
                        >
                          <KeyIcon className="h-5 w-5" />
                          <span className="lg:block">Đổi mật khẩu</span>
                        </Link>
                      </li>
                      <li>
                        <button
                          onClick={() => setShowLogoutConfirm(true)}
                          className="flex items-center gap-1"
                        >
                          <ArrowLeftOnRectangleIcon className="h-5 w-5 text-green-600" />
                          <span className="lg:block">Đăng xuất</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {hasPermission("CONTENT_MANAGE") && workspace == "admin" && (
                <button
                  className="lg:absolute lg:left-1 btn btn-info px-2 py-0 min-h-8 h-8"
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  aria-label="Toggle sidebar"
                >
                  {isSidebarOpen ? (
                    <ChevronDoubleLeftIcon className="h-6 w-6 text-white" />
                  ) : (
                    <Bars3Icon className="h-6 w-6 text-white" />
                  )}
                </button>
              )}

              <button
                className={`lg:absolute ${
                  hasPermission("CONTENT_MANAGE") && workspace == "admin"
                    ? "lg:left-14"
                    : "lg:left-8"
                } btn btn-info px-2 min-h-8 h-8`}
                onClick={() => {
                  navigate("/");
                  window.location.reload();
                }}
                aria-label="Go home"
              >
                <HomeIcon className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>
        </nav>

        {/* Main Page Content */}
        <main className="p-4 overflow-auto flex-1">
          <Outlet />
        </main>
      </div>

      {/* Xác nhận đăng xuất Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Xác nhận đăng xuất</h3>
            <p>Bạn có chắc chắn muốn đăng xuất khỏi hệ thống?</p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-focus"
              >
                Đồng ý
              </button>
            </div>
          </div>
        </div>
      )}

      {isUserModalOpen && (
        <UserModalComponent
          userIdProp={userId}
          onClose={handleCloseUserModal}
          onSave={handleSaveUser}
        />
      )}

      {isPartnerModalOpen && (
        <PartnerModal
          companyId={companyId || null}
          onClose={handleClosePartnerModal}
          onSubmit={{}}
          representList={[]}
        />
      )}
    </div>
  );
};

export default AdminLayout;